import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ActionMemoItem } from '../../api/actionMemoListApiTypes';
import {
    PendingProjectCommitmentItem,
    ProjectActualItem,
    ProjectCommitmentItem,
} from '../../api/projectBalanceApiTypes';
import {
    formatDate,
    formatCurrency,
    formatExportText,
    formatBooleanAsYesNo,
} from '../../utils/displayUtils';
import { EXPORT_DATE_FORMAT } from '../../utils/Constants';

const getTruncatedSubject = (subject: string, length: number = 30) =>
    subject
        .split(' ')
        .map((word) => (word.length > length ? `${word.substring(0, length - 3)}...` : word))
        .join(' ');

const generateSortingIndicator = (isSorted: boolean, isSortedDesc: boolean | undefined) => {
    if (isSorted) {
        return isSortedDesc ? <i className="fas fa-sort-down" /> : <i className="fas fa-sort-up" />;
    }
    return <i className="fas fa-sort" />;
};

const renderOverlay = (infoString: string) => (
    <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="tooltip">{infoString}</Tooltip>}
    >
        <i id="tooltip" className="fas fa-info-circle" />
    </OverlayTrigger>
);

const generateCSV = (list: object[], filename: string) => {
    const contents = [Object.keys(list[0])]
        .concat(list.map((item) => Object.values(item).toString()))
        .join('\n');
    generateDocument(contents, filename);
};

const generateDocument = (contents: string, filename: string) => {
    const documentBody: Element | null = document.body;
    if (documentBody) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
        a.download = filename;
        documentBody.appendChild(a);
        a.click();
        documentBody.removeChild(a);
    }
};

const exportAmList = (ams: ActionMemoItem[], filename: string) => {
    const list = ams.map((am) => ({
        'Action Memo #': am.amNumber,
        'Supplement #': am.supNumber,
        Status: am.amStatus,
        Subject: formatExportText(am.subject),
        Budget: `"${formatCurrency(am.budgetAmount)}"`,
        'Creation Date': formatDate(am.creationDate, EXPORT_DATE_FORMAT),
        'Completion Date': formatDate(am.completionDate, EXPORT_DATE_FORMAT),
        'Due Date': am.dueDate ? formatDate(am.dueDate, EXPORT_DATE_FORMAT) : '',
        'Approval Date': am.approvalDate ? formatDate(am.approvalDate, EXPORT_DATE_FORMAT) : '',
        Creator: formatExportText(am.amCreatorName),
        'Creator Email': am.amCreatorEmailAddress,
        'Project Manager': formatExportText(am.projectManagerName),
        'Project Manager Email': am.projectManagerEmailAddress,
        'Template Code': formatExportText(am.templateCode),
        'Template Description': formatExportText(am.templateDescription),
        'Organization Name': formatExportText(am.organizationName),
        'Current Approver': formatExportText(am.currentApproverName),
        'Current Approver Email': am.currentApproverEmailAddress,
        'Current Stop Number': am.currentStopNumber,
        'Last Stop Number': am.maxStopNumber,
        'Contract Signer': formatExportText(am.contractSignerName),
        'Contract Signer Email': am.contractSignerEmailAddress,
        Confidential: formatBooleanAsYesNo(am.confidential),
        Favorite: formatBooleanAsYesNo(am.watching),
    }));
    generateCSV(list, filename);
};

const exportCommitmentsList = (commitments: ProjectCommitmentItem[], filename: string) => {
    const list = commitments.map((item) => ({
        Date: item.date ? formatDate(item.date, EXPORT_DATE_FORMAT) : '',
        Source: item.source,
        'Transaction Number': item.commitmentNumber,
        'Line Number': item.commitmentLineNumber,
        'Shipment Number': item.shipmentNumber,
        Amount: `"${formatCurrency(item.amount)}"`,
    }));
    generateCSV(list, filename);
};

const exportPendingCommitmentsList = (
    pendingCommitments: PendingProjectCommitmentItem[],
    filename: string,
) => {
    const list = pendingCommitments.map((item) => ({
        Date: item.date ? formatDate(item.date, EXPORT_DATE_FORMAT) : '',
        Source: item.source,
        'Transaction Number': item.commitmentNumber,
        Amount: `"${formatCurrency(item.amount)}"`,
    }));
    generateCSV(list, filename);
};

const exportActualsList = (actuals: ProjectActualItem[], filename: string) => {
    const list = actuals.map((item) => ({
        Date: item.date ? formatDate(item.date, EXPORT_DATE_FORMAT) : '',
        Source: item.source,
        'Transaction Number': item.documentNumber,
        Vendor: item.vendorName,
        Amount: `"${formatCurrency(item.amount)}"`,
    }));
    generateCSV(list, filename);
};

export {
    generateSortingIndicator,
    generateCSV,
    getTruncatedSubject,
    renderOverlay,
    exportAmList,
    exportCommitmentsList,
    exportPendingCommitmentsList,
    exportActualsList,
};
